import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, TemplateResult, css, html } from 'lit';
import { customElement, query } from 'lit/decorators';

import { GetPointResponse } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import { debounce } from '../helpers/utils';
import { ifDefined } from 'lit/directives/if-defined';
import pointCache from '../helpers/state/point-cache';
import { pointSearch } from '../helpers/filters';
import topicData from '../helpers/state/topic-data';

@customElement('expanded-point-list')
export class ExpandedPointList extends LitElement {
    // This element is used on the discovery page to display a list of points and allow searching.
    amplitude_callsite: string = 'Expanded Point List';

    @query('#search')
    search!: TextField;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    protected render(): TemplateResult {
        return html`
            <div class="expanded-point-list__selectors">
                <mwc-textfield
                    id="search"
                    class="expanded-point-list__text-search"
                    label="Search"
                    @input="${this.searchChanged}">
                </mwc-textfield>
                <paper-tooltip for="search" offset="0">
                    Use this search field to filter by name, category, city, state, etc.
                </paper-tooltip>

                <mwc-select
                    id="categorySelect"
                    class="expanded-point-list__dropdown"
                    @selected="${this.filterByCategory}"
                    .value=${'allCategories'}
                    label="Category">
                    <mwc-list-item value="allCategories">All Categories</mwc-list-item>
                    ${topicData.categories.map((category) =>
                        html`<mwc-list-item value="${ifDefined(category._id?.$oid)}">${category.name}</mwc-list-item>`
                    )}
                </mwc-select>
            </div>
            <br />

            <point-card-pagination .discoveryView=${true} .showAllPoints=${true} .useHorizontalLayout=${false}></point-card-pagination>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    private readonly searchChanged = debounce(this.searchChangedInternal, 500);
    private searchChangedInternal() {
        const search = this.search.value?.trim().toLocaleLowerCase() ?? '';

        logWithAmplitude(this.amplitude_callsite, 'Search', { search });

        pointCache.removeFilter('ListSearch');
        if (search !== '') {
            pointCache.filterMarkers({ name: 'ListSearch', filterFn: (p: GetPointResponse) => pointSearch(p, search) });
            logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.MAP_SEARCH, {
                search_term: search,
            });
        }
    }

    private filterByCategory (e: Event) {
        const category = (e.target as HTMLInputElement).value; // this is a the category id

        pointCache.removeFilter('FilterChip');

        if (category !== 'allCategories') {
            const categoryFilter =  ((p: GetPointResponse) => {
                // Does the point's category match any of the selected categories.
                const pointCategory = topicData.categories.find((c) => c._id?.$oid === p.topic_category_id.$oid);
                const matches = category === pointCategory?._id?.$oid;

                return matches;
            });

            pointCache.filterMarkers({ name: 'FilterChip', filterFn: categoryFilter });
            logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.MAP_CATEGORY_CLICKED, {
                category_id: category,
                category_name: topicData.categories.find((c) => c._id?.$oid === category)?.name,
            });
        }
    };


    static get styles() {
        return css`
            .expanded-point-list__text-search {
                flex: 2;
                margin-right: 10px;
                width: 100%;
            }

            .expanded-point-list__selectors {
                margin-bottom: 20px;
                background-color: white;
                border-radius: 5px;
                padding: 5px;
            }

            .expanded-point-list__dropdown {
                flex: 1;
                width: 100%;
            }

            @media (min-width: 551px) {
                .expanded-point-list__selectors {
                    display: flex;
                    justify-content: space-between;
                }

            }

            @media only screen and (max-width: 550px) {
                .expanded-point-list__dropdown {
                    margin-top: 10px;
                }
            }
        `;
    }
}
